import { useState, type ReactNode } from 'react';
import { ChevronLeft, Maximize2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ExplainButton from './ExplainButton';
import { formatGeneratedAt } from './records-utils';

export interface SectionOverview {
  title: string;
  text: string;
  generatedAt: string;
}

interface SectionPageProps {
  title: string;
  patientFirstName: string;
  /** AI-written overview from the startup payload; omitted when there is none. */
  overview?: SectionOverview | null;
  explainQuestion: string;
  variant: 'main' | 'rail';
  onBack: () => void;
  onAskQuestion: (question: string) => void;
  onOpenFull?: () => void;
  children: ReactNode;
}

export default function SectionPage({ title, patientFirstName, overview, explainQuestion, variant, onBack, onAskQuestion, onOpenFull, children }: SectionPageProps) {
  const { t, i18n } = useTranslation();
  const [overviewExpanded, setOverviewExpanded] = useState(false);
  const isRail = variant === 'rail';

  const generatedAt = overview ? formatGeneratedAt(overview.generatedAt, i18n.resolvedLanguage || 'en') : null;

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 border-b bg-white">
        <div className={`${isRail ? 'px-4' : 'max-w-3xl mx-auto px-4 sm:px-6 md:pr-14'} py-2.5 flex items-center justify-between gap-2`}>
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary pr-1 flex-shrink-0"
              aria-label={t('records.backToAllRecords')}
              data-testid="button-records-back"
            >
              <ChevronLeft className="h-4 w-4" />
              <span className={isRail ? 'sr-only' : ''}>{t('records.backToAllRecords')}</span>
            </button>
            <h3 className="text-base font-semibold text-text-primary truncate">{title}</h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <ExplainButton
              onClick={() => onAskQuestion(explainQuestion)}
              ariaLabel={t('records.explainSection', { section: title })}
              testId="button-explain-section"
            />
            {isRail && onOpenFull && (
              <button
                onClick={onOpenFull}
                className="inline-flex items-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-gray-300 text-text-secondary hover:bg-gray-50"
                title={t('records.openFullTitle')}
                data-testid="button-open-full"
              >
                <Maximize2 className="h-3.5 w-3.5" />
                {t('records.openFull')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className={isRail ? 'px-4 py-4' : 'max-w-3xl mx-auto px-4 sm:px-6 py-5'}>
          {overview && overview.text && (
            <section className="rounded-lg border bg-gray-50 p-4 mb-4" data-testid="overview-card">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />
                <h3 className="text-sm font-semibold text-text-primary">{overview.title}</h3>
              </div>
              <p className={`text-base leading-relaxed text-text-primary ${overviewExpanded ? '' : 'line-clamp-3'}`}>{overview.text}</p>
              <button
                onClick={() => setOverviewExpanded(!overviewExpanded)}
                className="text-sm text-primary font-medium mt-1"
                aria-expanded={overviewExpanded}
                data-testid="button-overview-toggle"
              >
                {overviewExpanded ? t('records.showLess') : t('records.readMore')}
              </button>
              <p className="text-sm text-text-secondary mt-3">
                {generatedAt
                  ? t('records.writtenByAi', { name: patientFirstName, time: generatedAt })
                  : t('records.writtenByAiNoTime', { name: patientFirstName })}
              </p>
            </section>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
